import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Component, HostListener, ElementRef, OnInit, OnDestroy, Input, Optional, QueryList, ViewChildren} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {Location} from '@angular/common';
import {Subscription} from 'rxjs';
import {FormBuilder, Validators, FormGroup, FormArray, AbstractControl, ValidationErrors} from '@angular/forms';
import {TranslationService, TranslationEventService, Translatable, DateFormatService} from '@ngmedax/translation';
import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {LayoutService} from '@ngmedax/layout';
import {SimuService} from '@ngmedax/simu';
import {LoginService} from '@ngmedax/login';
import {WorkflowService} from '../services/workflow.service';
import {Workflow, Questionnaire} from '@ngmedax/common-questionnaire-types';
import {TRANSLATION_CRUD_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {analyseCondition, ConditionAnalysis, questionnaireOffersVariable} from './workflow-condition';
import {WorkflowStageConditionsComponent} from './conditions/workflow-stage-conditions.component';
import {buildConditionVariables, ConditionVariable} from './conditions/visual/workflow-condition-model';
import {WorkflowVariableDialogComponent} from './variables/workflow-variable-dialog.component';
import {mappingNameIssue, mappingUsage, renameMappingExpressions, VariableSelection, WorkflowMapping} from './variables/workflow-variables';
import {CustomFieldDefinition} from '../../../types';

interface EditorIssue { label: string; target: 'name' | 'steps' | 'questionnaires' | 'conditions' | 'variables'; index?: number; }
export interface WorkflowCrudComponent extends Translatable {}

function atLeastOneQuestionnaire(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (Array.isArray(value) && value.length > 0) {
    return null;
  }
  return {noQuestionnaires: true};
}

@Component({
  selector: 'app-workflow-crud',
  templateUrl: './workflow-crud.component.html',
  styleUrls: ['./workflow-crud.component.css']
})
@Translatable({ scope: TRANSLATION_CRUD_SCOPE, keys: KEYS })
export class WorkflowCrudComponent implements OnInit, OnDestroy {
  @Input() public workflowId: string | null = null;

  public workflowForm: FormGroup;
  public dirty = false;
  private savedSnapshot = '';
  public loading = false;
  public saving = false;
  public expandAllStages = false;
  private navigationTimer: ReturnType<typeof setTimeout> = null;
  private conditionAnalysis = new Map<string, ConditionAnalysis>();
  private issuesKey = '';
  private issues: EditorIssue[] = [];
  public showCloseWinBtn = false;
  private subscriptions: Subscription[] = [];
  public locale = 'de_DE';
  public collapsedStages: boolean[] = [];
  public activeTab: string = 'steps';
  @ViewChildren(WorkflowStageConditionsComponent) private conditionEditors: QueryList<WorkflowStageConditionsComponent>;
  private conditionSources: Questionnaire[] = [];
  private requestedConditionSources = new Set<string>();
  private catalogSources: Questionnaire[];
  private catalogKey = '';
  private catalog: ConditionVariable[] = [];
  private catalogQuestionnaires: Questionnaire[];
  private catalogCustomFields: CustomFieldDefinition[];

  public allQuestionnaires: Questionnaire[] = [];
  public filteredQuestionnaires: Questionnaire[] = [];

  private customFieldDefinitions: CustomFieldDefinition[] = [];
  private variableModal: NgbModalRef;
  public variableWarning = '';

  /** Opens the source picker from a condition draft without immediately changing the workflow. */
  public createConditionVariable = (pending: VariableSelection[]): Promise<VariableSelection> =>
    this.openVariableEditor(null, false, pending, true);

  constructor(
    private activatedRoute: ActivatedRoute,
    private modals: NgbModal,
    private formBuilder: FormBuilder,
    private workflowService: WorkflowService,
    private configService: ConfigService,
    private registryService: RegistryService,
    private layoutService: LayoutService,
    private simuService: SimuService,
    private loginService: LoginService,
    private location: Location,
    private translationEvents: TranslationEventService,
    private translationService: TranslationService,
    private dateFormatService: DateFormatService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  public ngOnInit(): void {
    this.translationService && (this.locale = this.translationService.getLocale());

    this.workflowForm = this.formBuilder.group({
      _id: [null],
      name: this.formBuilder.group({
        de_DE: ['', [Validators.required, Validators.pattern(/\S/)]]
      }),
      description: this.formBuilder.group({
        de_DE: ['']
      }),
      stages: this.formBuilder.array([]),
      variables: this.formBuilder.array([])
    });

    this.savedSnapshot = JSON.stringify(this.workflowForm.value);
    this.subscriptions.push(this.workflowForm.valueChanges.subscribe(() => { this.refreshDirty(); this.loadConditionSources(); }));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe((params: any) => params.closeWinBtn && (this.showCloseWinBtn = true)));
    const routeSub = this.activatedRoute.params.subscribe(async (params: any) => {
      if (params.id) {
        this.workflowId = params.id;
        await this.loadWorkflow(params.id);
      } else if (this.workflowId) {
        await this.loadWorkflow(this.workflowId);
      } else {
        this.dirty = false;
      }
    });
    this.subscriptions.push(routeSub);

    this.workflowService.loadQuestionnaires()
      .then(qs => {
        this.allQuestionnaires = Array.isArray(qs) ? qs : [];
        this.issuesKey = '';
        this.updateFilteredQuestionnaires();
      })
      .catch(() => {});

    // load custom field definitions for survey variables
    this.workflowService.loadCustomFieldDefinitions()
      .then(defs => this.customFieldDefinitions = defs)
      .catch(() => this.customFieldDefinitions = []);
  }

  public ngOnDestroy(): void {
    clearTimeout(this.navigationTimer);
    this.variableModal?.dismiss();
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  get stages(): FormArray {
    return this.workflowForm.get('stages') as FormArray;
  }

  get variables(): FormArray {
    return this.workflowForm.get('variables') as FormArray;
  }

  //

  private async loadWorkflow(workflowId: string) {
    try {
      this.loading = true;
      this.layoutService.showPreloader();
      const w = await this.workflowService.loadWorkflow(workflowId);

      if (w && this.simuService.isSimuMode()) {
        const user = await this.loginService.getUser();
        const userName = user.getUsername();
        const createdBy = w.createdBy;
        if (userName && createdBy && userName !== createdBy) {
          this.location.back();
        }
      }

      if (!w) {
        alert(this._(KEYS.CRUD.FOUND_NO_ENTRY_BY_ID) + ' ' + workflowId);
        return;
      }
      this.renderWorkflow(w);
      this.dirty = false;
      this.layoutService.hidePreloader();
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.layoutService.hidePreloader();
      alert(this._(KEYS.CRUD.ERROR_LOADING_WORKFLOW));
      this.loading = false;
    }
  }

  private renderWorkflow(workflow: Workflow) {
    this.workflowForm.reset();
    this.stages.clear();
    (this.workflowForm.get('variables') as FormArray).clear();
    this.collapsedStages = [];
    this.expandAllStages = false;

    this.workflowForm.get('_id')!.setValue(workflow._id || workflow.id || null);

    if (workflow.name) {
      for (const locale of Object.keys(workflow.name)) {
        const ctrl = this.workflowForm.get('name.' + locale);
        ctrl && ctrl.setValue(workflow.name[locale]);
      }
    }

    if (workflow.description) {
      for (const locale of Object.keys(workflow.description)) {
        const ctrl = this.workflowForm.get('description.' + locale);
        ctrl && ctrl.setValue(workflow.description[locale]);
      }
    }

    if (workflow.stages && workflow.stages.length) {
      workflow.stages.forEach((stage, index) => {
        const stageGroup = this.createStageFormGroup();
        if (stage.name) {
          for (const locale of Object.keys(stage.name)) {
            const ctrl = stageGroup.get('name.' + locale);
            ctrl && ctrl.setValue(stage.name[locale]);
          }
        }
        if (stage.description) {
          for (const locale of Object.keys(stage.description)) {
            const ctrl = stageGroup.get('description.' + locale);
            ctrl && ctrl.setValue(stage.description[locale]);
          }
        }

        stageGroup.get('autoStart')!.setValue(stage.autoStart || false);
        stageGroup.get('skipSigning')!.setValue(stage.skipSigning || false);
        stageGroup.get('generateExports')!.setValue(stage.generateExports || false);
        stageGroup.get('generateFormPDFs')!.setValue(stage.generateFormPDFs || false);
        stageGroup.get('prefill')!.setValue(stage.prefill || false);
        stageGroup.get('questionnaires')!.setValue(stage.questionnaires || []);
        stageGroup.get('expression')!.setValue(stage.expression || '');

        this.stages.push(stageGroup);
        this.collapsedStages.push(true);
      });
        // keep user choices; constraints will be warned on save only
    }

    const vars: any[] = (workflow as any).variables || [];
    if (Array.isArray(vars)) {
      for (const v of vars) {
        this.variables.push(this.createVariableFormGroup(v));
      }
    }

    this.updateFilteredQuestionnaires();

    this.savedSnapshot = JSON.stringify(this.workflowForm.value);
    this.refreshDirty();
  }

  /** Compares persisted form data with the last loaded or saved snapshot. */
  private refreshDirty(): void {
    this.dirty = JSON.stringify(this.workflowForm.value) !== this.savedSnapshot;
  }

  /** Requests the native browser warning when closing or reloading unsaved work. */
  @HostListener('window:beforeunload', ['$event'])
  public beforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty || this.saving) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Protects unsaved edits during navigation and waits for active saves. */
  public async canDeactivate(): Promise<boolean> {
    if (this.saving) {
      alert(this._(KEYS.CRUD.WAIT_FOR_SAVE));
      return false;
    }
    return !this.dirty || await confirm(this._(KEYS.CRUD.CONFIRM_LEAVE));
  }

  private createStageFormGroup(): FormGroup {
    return this.formBuilder.group({
      name: this.formBuilder.group({
        de_DE: ['']
      }),
      description: this.formBuilder.group({
        de_DE: ['']
      }),
      expression: [''],
      autoStart: [false],
      skipSigning: [false],
      generateExports: [false],
      generateFormPDFs: [false],
      prefill: [false],
      questionnaires: [[], atLeastOneQuestionnaire]
    });
  }

  /** Inserts a step at the requested position and opens its name field. */
  public onAddStage(index: number = this.stages.length): void {
    if (index < 0 || index > this.stages.length) {
      return;
    }
    this.stages.insert(index, this.createStageFormGroup());
    this.collapsedStages = this.stages.controls.map((_, i) => !this.expandAllStages && i !== index);
    this.activeTab = 'steps';
    this.refreshDirty();
    this.updateFilteredQuestionnaires();
    this.revealEditorTarget('workflow-stage-name-' + index);
  }

  /** Keeps panel state attached to controls, not their old positions. */
  private stageViewState() {
    return {
      collapsed: new Map(this.stages.controls.map((stage, i) => [stage, this.collapsedStages[i]]))
    };
  }

  /** Restores panel state after controls have moved or been removed. */
  private restoreStageView(state: ReturnType<WorkflowCrudComponent['stageViewState']>): void {
    this.collapsedStages = this.stages.controls.map(stage => state.collapsed.get(stage) ?? true);
    this.updateFilteredQuestionnaires();
  }

  /** Confirms removal of conditions when a step becomes the first step. */
  private async approveFirstStage(stage: AbstractControl): Promise<boolean> {
    const expression = stage?.get('expression')?.value;
    return !expression?.length || await confirm(this._(KEYS.CRUD.CONFIRM_FIRST_STAGE));
  }

  /** Applies a completed drag operation through the same safeguards as menu actions. */
  public dropStage(event: CdkDragDrop<AbstractControl[]>): void {
    void this.moveStage(event.previousIndex, event.currentIndex);
  }

  /** Moves an existing control while preserving its values and panel state. */
  public async moveStage(from: number, to: number): Promise<void> {
    if (from === to || from < 0 || to < 0 || from >= this.stages.length || to >= this.stages.length) { return; }
    const stage = this.stages.at(from);
    const nextFirst = to === 0 ? stage : (from === 0 ? this.stages.at(1) : null);
    if (nextFirst && !await this.approveFirstStage(nextFirst)) { return; }
    const state = this.stageViewState();
    if (nextFirst) { nextFirst.get('expression').setValue('', {emitEvent: false}); }
    this.stages.removeAt(from, {emitEvent: false});
    this.stages.insert(to, stage);
    this.restoreStageView(state);
    this.revealEditorTarget('workflow-step-' + to);
  }

  /** Inserts an independent copy directly after the selected step. */
  public duplicateStage(index: number): void {
    const original = this.stages.at(index);
    if (!original) { return; }
    const state = this.stageViewState();
    const copy = this.createStageFormGroup();
    copy.patchValue(JSON.parse(JSON.stringify(original.value)));
    copy.get('name.de_DE').setValue(this.getStageDisplayName(index) + ' (' + this._(KEYS.CRUD.COPY) + ')');
    this.stages.insert(index + 1, copy);
    this.restoreStageView(state);
    this.openStage(index + 1);
  }

  /** Removes a step after confirmation and checks the new first step. */
  public async onRemoveStage(index: number): Promise<void> {
    if (!this.stages.at(index) || !await confirm(this._(KEYS.CRUD.CONFIRM_DELETE_STAGE))) { return; }
    const nextFirst = index === 0 ? this.stages.at(1) : null;
    if (nextFirst && !await this.approveFirstStage(nextFirst)) { return; }
    const state = this.stageViewState();
    if (nextFirst) { nextFirst.get('expression').setValue('', {emitEvent: false}); }
    this.stages.removeAt(index);
    this.restoreStageView(state);
  }

  public async onSubmit() {
    await this.saveWorkflow();
  }

  private async saveWorkflow() {
    if (this.saving || this.loading || !this.workflowForm.valid || !this.stages.length) {
      return;
    }
    this.saving = true;
    const snapshot = JSON.stringify(this.workflowForm.value);
    try {
      const workflow: Workflow = JSON.parse(snapshot);
      const lastStage = this.stages.length > 0 ? (this.stages.at(this.stages.length - 1) as FormGroup) : null;
      if (lastStage) {
        const skipSigning = !!lastStage.get('skipSigning')?.value;
        const generateExports = !!lastStage.get('generateExports')?.value;
        const generateFormPDFs = !!lastStage.get('generateFormPDFs')?.value;
        if (skipSigning || !generateExports || !generateFormPDFs) {
          // alert(this._(KEYS.CRUD.WARNING_LAST_STAGE_CONSTRAINTS));
        }
      }
      let savedWorkflow: Workflow;

      if (workflow._id) {
        savedWorkflow = await this.workflowService.updateWorkflow(workflow._id, workflow);
      } else {
        savedWorkflow = await this.workflowService.createWorkflow(workflow);
      }

      if (JSON.stringify(this.workflowForm.value) === snapshot) {
        this.renderWorkflow(savedWorkflow);
        this.workflowForm.markAsPristine();
        this.dirty = false;
      } else {
        const id = savedWorkflow._id || savedWorkflow.id;
        this.workflowForm.get('_id').setValue(id, {emitEvent: false});
        this.savedSnapshot = JSON.stringify({...JSON.parse(snapshot), _id: id});
        this.refreshDirty();
      }
      alert(this._(KEYS.CRUD.SUCCESSFULLY_SAVED_WORKFLOW));
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.CRUD.ERROR_SAVING_WORKFLOW));
    } finally {
      this.saving = false;
    }
  }

  public onQuestionnairesSelect(questionnaires: string[], stageIndex: number) {
    const stage = this.stages.at(stageIndex) as FormGroup;
    stage.get('questionnaires')!.setValue(questionnaires);
    this.refreshDirty();
    this.updateFilteredQuestionnaires();
  }

  /** Opens one step at a time unless the comparison view is enabled. */
  public toggleStage(index: number): void {
    const opening = this.collapsedStages[index];
    if (opening && !this.expandAllStages) {
      this.collapsedStages = this.stages.controls.map(() => true);
    }
    this.collapsedStages[index] = !opening;
  }

  /** Toggles between the full comparison view and a compact step overview. */
  public toggleAllStages(): void {
    this.expandAllStages = !this.expandAllStages;
    this.collapsedStages = this.stages.controls.map(() => !this.expandAllStages);
  }

  /** Keeps step views associated with their form controls when inserting between them. */
  public trackStage(index: number, stage: AbstractControl): AbstractControl {
    return stage;
  }

  /** Caches syntax analysis for repeated summary and validation rendering. */
  public getConditionAnalysis(index: number): ConditionAnalysis {
    const expression = this.stages.at(index)?.get('expression')?.value;
    const key = JSON.stringify(expression);
    if (!this.conditionAnalysis.has(key)) {
      if (this.conditionAnalysis.size > 100) { this.conditionAnalysis.clear(); }
      this.conditionAnalysis.set(key, analyseCondition(expression));
    }
    return this.conditionAnalysis.get(key);
  }

  /** Returns the number of comparisons or a marker for unsupported conditions. */
  public getConditionCount(index: number): number {
    const analysis = this.getConditionAnalysis(index);
    return analysis.error ? -1 : analysis.count;
  }

  /** Loads full source definitions once per mapped questionnaire because the grid response omits question content. */
  private loadConditionSources(): void {
    const ids: string[] = this.variables.value.filter(variable => variable.target?.type === 'questionnaire')
      .map(variable => variable.target.id).filter(Boolean);
    for (const id of ids) {
      if (this.requestedConditionSources.has(id)) { continue; }
      this.requestedConditionSources.add(id);
      this.workflowService.loadQuestionnaire(id).then(questionnaire => {
        if (questionnaire) { this.conditionSources = [...this.conditionSources, questionnaire]; }
      }).catch(error => console.warn('Could not load workflow condition source', id, error));
    }
  }

  /** Resolves current source names once per mapping or catalog update for all step previews and dialogs. */
  public get conditionVariables(): ConditionVariable[] {
    const key = JSON.stringify(this.variables.value) + this.locale;
    if (key !== this.catalogKey || this.catalogQuestionnaires !== this.allQuestionnaires ||
        this.catalogCustomFields !== this.customFieldDefinitions || this.catalogSources !== this.conditionSources) {
      this.catalogKey = key;
      this.catalogQuestionnaires = this.allQuestionnaires;
      this.catalogCustomFields = this.customFieldDefinitions;
      this.catalogSources = this.conditionSources;
      const questionnaires = Array.from(new Map([...this.allQuestionnaires, ...this.conditionSources]
        .map(questionnaire => [questionnaire.id, questionnaire])).values());
      this.catalog = buildConditionVariables(this.workflowForm.value, questionnaires,
        this.customFieldDefinitions, this.locale, text => this._(text));
    }
    return this.catalog;
  }

  /** Collects actionable hints without recreating unchanged entries during rendering. */
  public get editorIssues(): EditorIssue[] {
    const key = JSON.stringify(this.workflowForm.value) + this.locale;
    if (key === this.issuesKey) { return this.issues; }
    this.issuesKey = key;
    const issues: EditorIssue[] = [];
    if (!this.workflowForm.get('name.de_DE').value?.trim()) {
      issues.push({label: this._(KEYS.CRUD.MISSING_NAME), target: 'name'});
    }
    if (!this.stages.length) { issues.push({label: this._(KEYS.CRUD.MISSING_STEPS), target: 'steps'}); }
    const variables = this.variables.value as any[];
    this.stages.controls.forEach((stage, index) => {
      const prefix = this._(KEYS.CRUD.STAGE) + ' ' + (index + 1) + ': ';
      if (!stage.get('questionnaires').value?.length) {
        issues.push({label: prefix + this._(KEYS.CRUD.NO_QUESTIONNAIRES), target: 'questionnaires', index});
      }
      if (!index) { return; }
      const analysis = this.getConditionAnalysis(index);
      if (analysis.error) {
        issues.push({label: prefix + this._(KEYS.CRUD.INVALID_CONDITION), target: 'conditions', index});
      }
      for (const name of analysis.references) {
        const variable = variables.find(v => v.name === name);
        if (!variable) {
          issues.push({label: prefix + name + ': ' + this._(KEYS.CRUD.MISSING_VARIABLE), target: 'variables'});
        } else if (variable.target?.type === 'questionnaire' &&
          !this.stages.controls.slice(0, index).some(s => s.get('questionnaires').value.some(id =>
            questionnaireOffersVariable(this.allQuestionnaires.find(q => q.id === id), variable.target.variable)))) {
          issues.push({label: prefix + name + ': ' + this._(KEYS.CRUD.LATE_VARIABLE), target: 'conditions', index});
        }
      }
    });
    this.issues = issues;
    return issues;
  }

  /** Opens and focuses the editor area associated with a validation hint. */
  public openIssue(issue: EditorIssue): void {
    switch (issue.target) {
      case 'name': this.revealEditorTarget('workflow-name'); break;
      case 'steps': this.activeTab = 'steps'; this.revealEditorTarget('workflow-add-step'); break;
      case 'conditions': this.openStageConditions(issue.index); break;
      case 'variables': this.activeTab = 'variables'; this.revealEditorTarget('workflow-variable-name'); break;
      case 'questionnaires':
        this.openStage(issue.index);
        this.revealEditorTarget('workflow-questionnaires-' + issue.index);
        break;
    }
  }

  /** Opens the selected step's condition dialog directly from a validation hint. */
  public openStageConditions(index: number): void {
    if (index <= 0 || index >= this.stages.length) { return; }
    this.activeTab = 'steps';
    this.conditionEditors?.find(editor => editor.stageIndex === index)?.edit();
  }

  /** Returns to the corresponding step from its conditions. */
  public openStage(index: number): void {
    this.activeTab = 'steps';
    if (!this.expandAllStages) {
      this.collapsedStages = this.stages.controls.map(() => true);
    }
    this.collapsedStages[index] = false;
    this.revealEditorTarget('workflow-stage-name-' + index);
  }

  /** Reveals a newly rendered target and gives keyboard users a useful focus location. */
  private revealEditorTarget(id: string): void {
    clearTimeout(this.navigationTimer);
    this.navigationTimer = setTimeout(() => {
      const target = this.elementRef.nativeElement.querySelector<HTMLElement>('#' + id);
      target?.focus({preventScroll: true});
      target?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, 0);
  }

  public getStageDisplayName(index: number): string {
    const nameCtrl = this.stages.at(index).get('name.de_DE');
    const val = (nameCtrl && typeof (nameCtrl as any).value === 'string') ? (nameCtrl as any).value as string : '';
    const name = val && val.trim() ? val.trim() : null;
    if (name) {
      return name;
    }
    return this._(KEYS.CRUD.STAGE) + ' ' + (index + 1);
  }

  public _!: (text: string, variables?: any) => string;
  public getDateFormat!: (format: string, locale?: string) => string;
  public readonly KEYS = KEYS;

  /**
   * Creates a form group representing a workflow variable mapping.
   */
  private createVariableFormGroup(initial?: any): FormGroup {
    return this.formBuilder.group({
      name: [initial?.name || '', [Validators.required]],
      target: this.formBuilder.group({
        type: [initial?.target?.type || 'questionnaire', [Validators.required]],
        id: [initial?.target?.id || ''],
        variable: [initial?.target?.variable || '', [Validators.required]],
        options: [initial?.target?.options || null]
      })
    });
  }

  /**
   * Updates the filtered questionnaires list based on questionnaires used in stages.
   */
  private updateFilteredQuestionnaires(): void {
    const ids: string[] = [];
    for (let i = 0; i < this.stages.length; i++) {
      const q = (this.stages.at(i) as FormGroup).get('questionnaires')!.value as string[];
      if (Array.isArray(q)) {
        for (const id of q) {
          if (id && !ids.includes(id)) {
            ids.push(id);
          }
        }
      }
    }
    this.filteredQuestionnaires = this.allQuestionnaires.filter(q => ids.includes(q.id));
  }

  /** Opens a transactional source mapping dialog, optionally including variables from an outer condition draft. */
  private async openVariableEditor(name: string, deleting = false, pending: VariableSelection[] = [],
    inCondition = false): Promise<VariableSelection> {
    if (this.variableModal) { return null; }
    const workflow = JSON.parse(JSON.stringify(this.workflowForm.value));
    workflow.variables.push(...pending.map(selection => selection.variable));
    const modal = this.modals.open(WorkflowVariableDialogComponent, {
      centered: true, size: 'lg', windowClass: 'workflow-condition-modal', ariaLabelledBy: 'workflow-variable-title'
    });
    this.variableModal = modal;
    Object.assign(modal.componentInstance, {workflow, deleting, inCondition, original: workflow.variables.find(variable => variable.name === name),
      questionnaires: Array.from(new Map([...this.allQuestionnaires, ...this.conditionSources].map(q => [q.id, q])).values()),
      customFields: this.customFieldDefinitions, locale: this.locale});
    try { return await modal.result; }
    catch { return null; }
    finally { this.variableModal = null; }
  }

  /** Applies mapping changes and identifier references together after checking for intervening form edits. */
  public async editVariable(name: string = null, deleting = false): Promise<void> {
    const original = JSON.stringify(this.workflowForm.value);
    const selection = await this.openVariableEditor(name, deleting);
    if (!selection) { return; }
    if (original !== JSON.stringify(this.workflowForm.value)) {
      this.variableWarning = 'Der Workflow wurde zwischenzeitlich geändert. Bitte öffnen Sie den Editor erneut.';
      return;
    }
    this.variableWarning = '';
    const index = this.variables.controls.findIndex(control => control.get('name').value === name);
    if (deleting) {
      const usage = mappingUsage(this.workflowForm.value, name);
      if (index < 0 || usage.unknown || usage.indices.length) { return; }
      this.variables.removeAt(index);
      return;
    }
    if (mappingNameIssue(selection.variable.name, this.variables.value, name)) { return; }
    const expressions = name ? renameMappingExpressions(this.workflowForm.value, name, selection.variable.name) : null;
    if (index >= 0) { this.variables.at(index).patchValue(selection.variable, {emitEvent: false}); }
    else { this.variables.push(this.createVariableFormGroup(selection.variable), {emitEvent: false}); }
    expressions?.forEach((expression, stage) => this.stages.at(stage).get('expression').setValue(expression, {emitEvent: false}));
    this.workflowForm.updateValueAndValidity();
    this.workflowForm.markAsDirty();
  }

  /** Applies newly mapped sources together with the condition that uses them. */
  public onStageConditionChange(index: number, change: {expression: string; addedVariables: WorkflowMapping[]}): void {
    const variables = [...this.variables.value];
    for (const variable of change.addedVariables) {
      if (mappingNameIssue(variable.name, variables)) { return; }
      variables.push(variable);
    }
    change.addedVariables.forEach(variable => this.variables.push(this.createVariableFormGroup(variable), {emitEvent: false}));
    this.stages.at(index).get('expression').setValue(change.expression, {emitEvent: false});
    this.workflowForm.updateValueAndValidity();
    this.workflowForm.markAsDirty();
  }

  /**
   * Returns questionnaire title for the current locale by id.
   */
  public getQuestionnaireTitle(id: string): string {
    let q = this.allQuestionnaires.find(x => x.id === id);
    if (!q) {
      q = this.filteredQuestionnaires.find(x => x.id === id) as any;
    }
    const titles = (q && (q as any).meta && (q as any).meta.title) || {};
    const deTitle = titles['de_DE'] || (Object.values(titles)[0] as string) || '';
    return deTitle || id || '';
  }

}
